import React from "react";
import { ControlPanel } from "@/components/ControlPanel";
import { ScoreboardDisplay } from "@/components/scoreboard/ScoreboardDisplay";
import { ScoreboardControls } from "@/components/scoreboard/ScoreboardControls";
import { GameSelector } from "@/components/GameSelector";
import { TSPEvent } from "@/types/tsp";
import { useMessages } from "@/hooks/useMessages";
import { useAlerts } from "@/hooks/useAlerts";
import { useScoreboardState } from "@/hooks/useScoreboardState";
import { useMarkets } from "@/hooks/useMarkets";
import { useKitMarket } from "@/hooks/useKitMarket";
import { toast } from "sonner";

interface KitControlContentProps {
  kitId: string;
  kitNumber: number;
  viewMode: "ticker" | "scoreboard";
  selectedGame?: TSPEvent;
  isHeaderCollapsed: boolean;
}

export function KitControlContent({
  kitId,
  kitNumber,
  viewMode,
  selectedGame,
  isHeaderCollapsed,
}: KitControlContentProps) {
  const {
    teamAScore,
    setTeamAScore,
    teamBScore,
    setTeamBScore,
    teamAHits,
    setTeamAHits,
    teamAErrors,
    setTeamAErrors,
    teamBHits,
    setTeamBHits,
    teamBErrors,
    setTeamBErrors,
    balls,
    setBalls,
    strikes,
    setStrikes,
    outs,
    setOuts,
    count,
    setCount,
    isTopInning,
    setIsTopInning,
    resetCount,
    baseRunners,
    setBaseRunnerChange,
    teamAName,
    teamAPrimaryColor,
    teamASecondaryColor,
    teamALogoUrl,
    teamBName,
    teamBPrimaryColor,
    teamBSecondaryColor,
    teamBLogoUrl,
    updateTeamInfo,
  } = useScoreboardState(kitNumber?.toString());

  const {
    messages,
    allMessages,
    addMessageMutation,
    updateMessageMutation,
    toggleMessageActiveMutation,
    removeMessageMutation,
  } = useMessages(kitId);

  const {
    alerts,
    addAlertMutation,
    updateAlertMutation,
    toggleAlertActiveMutation,
    removeAlertMutation,
  } = useAlerts(kitId);

  const { data: markets = [] } = useMarkets();
  const { kitMarket, updateKitMarketMutation } = useKitMarket(kitId);

  const handleGameSelect = (game: TSPEvent) => {
    console.log("Selected game:", game);
    
    updateTeamInfo({
      team_a_name: game.meta.event_away_team_abbreviation,
      team_a_primary_color: game.meta.event_away_team_primary_color,
      team_a_secondary_color: game.meta.event_away_team_secondary_color,
      team_a_logo_url: game.meta.event_away_team_large_logo,
      team_b_name: game.meta.event_home_team_abbreviation,
      team_b_primary_color: game.meta.event_home_team_primary_color,
      team_b_secondary_color: game.meta.event_home_team_secondary_color,
      team_b_logo_url: game.meta.event_home_team_large_logo,
    });

    toast.success(`Updated scoreboard with ${game.meta.event_away_team_abbreviation} vs ${game.meta.event_home_team_abbreviation}`);
  };

  const handleUpdateKitMarket = (marketShortName: string) => {
    updateKitMarketMutation.mutate(marketShortName, {
      onSuccess: () => {
        toast.success(`Updated market to ${marketShortName}`);
      },
      onError: (error) => {
        console.error("Error updating market:", error);
        toast.error("Failed to update market");
      }
    });
  };

  if (viewMode === "ticker") {
    return (
      <ControlPanel 
        kitId={kitId}
        activeKit={kitId}
        messages={messages}
        allMessages={allMessages}
        alerts={alerts}
        markets={markets}
        kitMarket={kitMarket}
        onAddMessage={addMessageMutation.mutate}
        onRemoveMessage={removeMessageMutation.mutate}
        onUpdateMessage={(id, text) => updateMessageMutation.mutate({ id, text })}
        onToggleMessageActive={(id, isActive) => toggleMessageActiveMutation.mutate({ messageId: id, isActive })}
        onAddAlert={addAlertMutation.mutate}
        onRemoveAlert={removeAlertMutation.mutate}
        onUpdateAlert={(id, text) => updateAlertMutation.mutate({ id, text })}
        onToggleAlertActive={(id, isActive) => toggleAlertActiveMutation.mutate({ id, isActive })}
        onUpdateKitMarket={handleUpdateKitMarket}
      />
    );
  }

  return (
    <div className="bg-[#1A1F2C] rounded-lg p-2 sm:p-4">
      <div className="grid grid-cols-1 gap-6">
        {!isHeaderCollapsed && (
          <div className="bg-[#1A1F2C]/80 rounded-lg p-4 backdrop-blur-sm border border-white/10">
            <GameSelector 
              kitNumber={kitNumber}
              onGameSelect={handleGameSelect}
            />
          </div>
        )}
        
        <div className="mb-4 sm:mb-6">
          <ScoreboardDisplay 
            teamAScore={teamAScore}
            teamBScore={teamBScore}
            teamAHits={teamAHits}
            teamAErrors={teamAErrors}
            teamBHits={teamBHits}
            teamBErrors={teamBErrors}
            balls={balls}
            strikes={strikes}
            outs={outs}
            count={count}
            isTopInning={isTopInning}
            baseRunners={baseRunners}
            teamAName={teamAName}
            teamAPrimaryColor={teamAPrimaryColor}
            teamASecondaryColor={teamASecondaryColor}
            teamALogoUrl={teamALogoUrl}
            teamBName={teamBName}
            teamBPrimaryColor={teamBPrimaryColor}
            teamBSecondaryColor={teamBSecondaryColor}
            teamBLogoUrl={teamBLogoUrl}
            onBallsChange={setBalls}
            onStrikesChange={setStrikes}
          />
        </div>
        
        <div>
          <ScoreboardControls 
            teamAScore={teamAScore}
            teamBScore={teamBScore}
            teamAHits={teamAHits}
            teamAErrors={teamAErrors}
            teamBHits={teamBHits}
            teamBErrors={teamBErrors}
            balls={balls}
            strikes={strikes}
            outs={outs}
            count={count}
            isTopInning={isTopInning}
            baseRunners={baseRunners}
            selectedGame={selectedGame}
            onTeamAScoreChange={setTeamAScore}
            onTeamBScoreChange={setTeamBScore}
            onTeamAHitsChange={setTeamAHits}
            onTeamAErrorsChange={setTeamAErrors}
            onTeamBHitsChange={setTeamBHits}
            onTeamBErrorsChange={setTeamBErrors}
            onBallsChange={setBalls}
            onStrikesChange={setStrikes}
            onOutsChange={setOuts}
            onCountChange={setCount}
            onInningHalfChange={setIsTopInning}
            onResetCount={resetCount}
            onBaseRunnerChange={setBaseRunnerChange}
          />
        </div>
      </div>
    </div>
  );
}
